/**
 * User Info Composable
 * 使用者資訊管理
 * 原始來源: machines/user_info.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/user/userInfo.js)
 * - 業務邏輯：保留在 composable 中
 * - 單例模式：使用 nuxtApp._userInfoContext 避免閉包累積
 */

import { storeToRefs } from 'pinia'
import { ref, computed } from 'vue'

import { userApi, activityApi } from '@/api'
import { useIdentityScopeStore } from '@/stores/auth/identityScope'
import { useUserInfoStore } from '@/stores/user/userInfo'
import { isBirthdayOn } from '@/utils/birthday'
import { identityKey } from '@/utils/identityGuard'

export const useUserInfo = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保閉包只創建一次，避免 SSR 環境下記憶體洩漏
  if (!nuxtApp._userInfoContext) {
    const { $request } = nuxtApp
    const { appSecret } = useRuntimeConfig().public

    const store = useUserInfoStore()
    const identityScope = useIdentityScopeStore()
    const { token } = useAuthToken()

    // 使用者世代：resetUserState()（登出）會遞增，讓「登出前已送出、尚未回來」的
    // fetchUserInfo / fetchProfile 回應在 await 之後發現自己已過期而放棄寫回 store，
    // 避免舊帳號資料殘留、被下一個帳號誤用（例如生日判斷 fallback 到殘留的 profileGroup）
    let generation = 0

    // API Methods - 只創建一次
    const fetchUserInfo = async () => {
      const requestGeneration = generation
      // /self 的回應只能證明「發出當下那個 token」屬於誰（spec 2026-10-19 REQ-2/3）
      const requestTokenKey = identityKey(token.value)
      store.setStatus('fetching')
      store.setError(null)

      try {
        const response = await $request.send(userApi.getUserInfo())
        const data = response.data || response

        if (requestGeneration === generation) {
          store.setUserInfo(data || {})
          if (requestTokenKey && requestTokenKey === identityKey(token.value)) {
            identityScope.confirm({ tokenKey: requestTokenKey, uidSite: data?.uid_site })
          }
        }
        store.setStatus('idle')
        return data
      } catch (err) {
        store.setError(err)
        store.setStatus('idle')
        throw err
      }
    }

    const updateUserInfo = async (data) => {
      store.setStatus('updating')
      store.setError(null)

      try {
        const response = await $request.send(userApi.updateUserInfo(data))
        const result = response.data || response

        // Note: 因為更新 userInfo 後 get self 還是拿到舊資料
        // 所以這裡不重新 fetch，而是直接更新 state
        // 如果需要最新資料，可以手動呼叫 fetchUserInfo

        store.setStatus('idle')
        return result
      } catch (err) {
        store.setError(err)
        store.setStatus('idle')
        throw err
      }
    }

    const setUserInfo = (data) => {
      store.setUserInfo(data)
    }

    const setProfileGroup = (data) => {
      store.setProfileGroup(data)
    }

    // 登出專用：清空狀態並遞增世代，讓還飛行中的舊請求回來時放棄寫回（見上方 generation 註解）；
    // 同時結束身分作用域，清空歸屬紀錄並重置 user-scoped store（spec 2026-10-19 REQ-5）
    const resetUserState = () => {
      generation += 1
      store.setUserInfo({})
      store.setProfileGroup({})
      identityScope.end()
    }

    const fetchLevelBonusInfo = async () => {
      const { data } = await $request.send(activityApi.getLevelBonusInfo({ appSecret }))
      store.setLevelBonusInfo(data)
    }

    const fetchProfile = async () => {
      const requestGeneration = generation
      store.setStatus('fetchingProfile')
      store.setError(null)

      try {
        const response = await $request.send(userApi.getUserProfile())
        const data = response.data || response

        if (requestGeneration === generation) {
          store.setProfileGroup(data || {})
        }
        store.setStatus('idle')
        return data
      } catch (err) {
        store.setError(err)
        store.setStatus('idle')
        throw err
      }
    }

    // 從 store 解構的 refs
    const storeRefs = storeToRefs(store)

    // 供 isBirthdayToday 跨午夜重算的低頻時間依賴（僅 client 端啟動）。
    // 必須用站點時區：原本用 new Date() 只會在「裝置午夜」翻轉，
    // 但比對基準是 $dayjs（站點時區），兩者不同步時 computed 不會在站點午夜失效。
    const todayKey = ref('')
    let todayTimer = null
    let dayjsWarmupTimer = null
    if (import.meta.client) {
      const syncToday = () => {
        if (!nuxtApp.$dayjs) return false
        todayKey.value = nuxtApp.$dayjs().format('YYYY-MM-DD')
        return true
      }
      // $dayjs 由 dayjs.client.js 注入，可能晚於本 composable 的首次呼叫——
      // 數字前綴的 analytics plugin（如 09.umami.client.js）依檔名順序先執行且會呼叫 useUserInfo()。
      // 此時若只留空字串等 60 秒後的 interval，登入後首分鐘所有生日標示都會消失。
      if (!syncToday()) {
        dayjsWarmupTimer = setInterval(() => {
          if (syncToday()) {
            clearInterval(dayjsWarmupTimer)
            dayjsWarmupTimer = null
          }
        }, 200)
      }
      todayTimer = setInterval(syncToday, 60 * 1000)
    }

    nuxtApp._userInfoContext = {
      // Store refs
      status: storeRefs.status,
      userInfo: storeRefs.userInfo,
      profileGroup: storeRefs.profileGroup,
      error: storeRefs.error,
      loading: storeRefs.loading,
      isFetching: storeRefs.isFetching,
      isUpdating: storeRefs.isUpdating,
      getUserInfo: storeRefs.getUserInfo,
      hasUserInfo: storeRefs.hasUserInfo,
      isVerified: storeRefs.isVerified,
      isSettleEnable: storeRefs.isSettleEnable,
      phoneVerificationStatus: storeRefs.phoneVerificationStatus,
      emailVerificationStatus: storeRefs.emailVerificationStatus,

      // Computed - 只創建一次
      uidSite: computed(() => storeRefs.userInfo.value?.uid_site),
      userAvatar: computed(() => {
        const { photo } = storeRefs.userInfo.value || {}
        const { user } = typeof window !== 'undefined' ? window?.Telegram?.WebApp?.initDataUnsafe || {} : {}
        return user?.photo_url || photo
      }),
      userNickname: computed(() => {
        const { nickname } = storeRefs.userInfo.value || {}
        const { user } = typeof window !== 'undefined' ? window?.Telegram?.WebApp?.initDataUnsafe || {} : {}
        return user?.username || nickname
      }),
      isBirthdayToday: computed(() => {
        // $dayjs 為 client-only plugin，SSR 一律視為非壽星，待 client 端 userInfo 更新後才顯示
        if (import.meta.server || !nuxtApp.$dayjs) return false
        // 讀取 todayKey 同時建立跨午夜的響應式依賴（每分鐘以站點時區校時），
        // 避免 computed 快取造成長 session 顯示錯誤
        const birthday = storeRefs.userInfo.value?.birthday || storeRefs.profileGroup.value?.birthday || ''
        return isBirthdayOn(birthday, todayKey.value)
      }),

      // Methods
      fetchUserInfo,
      updateUserInfo,
      setUserInfo,
      setProfileGroup,
      resetUserState,
      fetchLevelBonusInfo,
      fetchProfile,

      // cleanup 方法：SSR 由 ssr-cleanup.server.js 調用；
      // client 端該 plugin 不執行，故另於下方掛 app:unmount（沿用專案慣例）
      cleanup: () => {
        if (todayTimer) {
          clearInterval(todayTimer)
          todayTimer = null
        }
        if (dayjsWarmupTimer) {
          clearInterval(dayjsWarmupTimer)
          dayjsWarmupTimer = null
        }
      },
    }

    // client 端的 timer 沒有任何路徑會被 ssr-cleanup.server.js 清除；
    // Nuxt app 卸載（含測試 / HMR 重建）後舊 timer 仍持有 nuxtApp、store refs 與 computed context
    if (import.meta.client) {
      nuxtApp.hook('app:unmount', () => nuxtApp._userInfoContext?.cleanup?.())
    }
  }

  return nuxtApp._userInfoContext
}
